import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import has from 'lodash/has';
import isArray from 'lodash/isArray';

import api, { openApi } from '../../services/api';
import {
  setLogoutHandler,
  writeTokens,
  clearTokens,
  readToken,
  isOnPublicRoute,
} from '../../services/authSession';
import toastError from '../../errors/toastError';
import { unsubscribeAndUnregister } from '../../services/pushSubscriptionService';
import { socketConnection } from '../../services/socket';
import { clearPlatformProvidersCache } from '../../services/aiCreditsService';
import { verifyTwoFactorChallenge } from '../../services/twoFactorService';

const AUTH_SAFETY_TIMEOUT_MS = 8000; // garante que loading=false mesmo em rede travada

const USER_CACHE_KEY = 'cached_user_v1';

export const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

export const writeCachedUser = (u) => {
  try {
    if (u && u.id) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
  } catch (_) {
    // silent
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch (_) {
    // silent
  }
};

/**
 * Resolve o array flat de permissions para quem respeita RBAC (todos exceto owner/super).
 * O backend pode ou não incluir `permissions` no /auth/me; se não incluir,
 * buscamos de /users/:id/permissions (role + overrides) para popular o campo.
 */
export const resolvePermissions = async (userData) => {
  if (!userData?.id) return userData;
  if (userData.super || userData.isOwner) return userData;
  if (userData.permissions !== undefined && userData.permissions !== null) return userData;

  try {
    const { data } = await api.get(`/users/${userData.id}/permissions`);
    const perms = new Set(data.rolePermissions ?? []);
    for (const o of data.overrides ?? []) {
      if (o.granted) perms.add(o.permissionKey);
      else perms.delete(o.permissionKey);
    }
    return {
      ...userData,
      permissions: [...perms],
      ticketWhatsappIds: data.ticketWhatsappIds ?? [],
    };
  } catch {
    return userData;
  }
};

const useAuth = () => {
  const history = useHistory();
  const splashTimeoutRef = useRef(null);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(() => readCachedUser());
  const [socket, setSocket] = useState(null);
  const [loginOrigin, setLoginOrigin] = useState(
    () => localStorage.getItem('loginOrigin') || 'default'
  );

  const doLogout = () => {
    clearTokens();
    clearCachedUser();
    clearPlatformProvidersCache();
    setIsAuth(false);
    setUser({});
    // Página pública (link do mapa, formulário, agendamento): a sessão vencida
    // some, mas o visitante fica — a página não precisa de login.
    if (isOnPublicRoute()) return;
    const origin = localStorage.getItem('loginOrigin') || 'default';
    history.push(origin === 'mobile' ? '/mobile-login' : '/login');
  };

  // Os interceptors de auth vivem em services/api.js, em escopo de módulo — não
  // podem depender de um effect do React, porque providers filhos disparam
  // requests antes dos effects do pai rodarem (bug do 401 sem Authorization).
  // Aqui só entregamos o doLogout, que precisa do history e dos setters.
  useEffect(() => {
    setLogoutHandler(doLogout);
    return () => setLogoutHandler(null);
    // doLogout só usa history/localStorage/setters estáveis — deps vazias de propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = readToken();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      setLoading(false);
      if (splashTimeoutRef.current) {
        clearTimeout(splashTimeoutRef.current);
        splashTimeoutRef.current = null;
      }
    };

    splashTimeoutRef.current = setTimeout(finish, AUTH_SAFETY_TIMEOUT_MS);

    (async () => {
      if (token) {
        try {
          const { data } = await api.get('/auth/me');
          setIsAuth(true);
          const resolved = await resolvePermissions(data);
          setUser(resolved);
          writeCachedUser(resolved);
        } catch (err) {
          // 401 aqui já passou pelo refresh no interceptor e falhou: sessão morta.
          // O interceptor chama handleSessionExpired → doLogout, então só não
          // marcamos a sessão como autenticada. Antes este catch era silencioso
          // quando havia cache, e o app renderizava logado com todas as chamadas
          // tomando 401 e sem nunca cair no login (bug corrigido).
          if (err?.response?.status !== 401) {
            // Backend offline/erro de rede: mantém o user do cache (restaurado no
            // useState inicial) em vez de derrubar a sessão.
            setIsAuth(true);
            if (!readCachedUser()?.id) toastError(err);
          }
        }
      }
      finish();
    })();

    return () => {
      if (splashTimeoutRef.current) clearTimeout(splashTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;
      if (!socket) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }
      const eventName = `company-${user.companyId}-user`;

      const handleUserEvent = async (data) => {
        // NÃO tratar action "presence" aqui: é evento de status online/away/offline
        // com payload mínimo. Tratá-lo como "update" refaria GET /auth/me a cada
        // transição de presença de qualquer usuário (storm de requests — já ocorreu).
        if (data.action === 'update' && Number(data.user?.id) === Number(user.id)) {
          if (Array.isArray(data.user.permissions)) {
            // Backend incluiu permissions no evento — merge defensivo preservando trial/plan
            const incoming = data.user;
            const merged = { ...user, ...incoming };
            if (incoming.trial === undefined && user.trial !== undefined) {
              merged.trial = user.trial;
            }
            if (
              incoming.company !== undefined &&
              incoming.company?.plan === undefined &&
              user.company?.plan !== undefined
            ) {
              merged.company = { ...merged.company, plan: user.company.plan };
            }
            setUser(merged);
            writeCachedUser(merged);
          } else {
            // Backend não incluiu permissions no socket — /auth/me já traz permissions efetivas
            const fresh = await getCurrentUserInfo();
            if (fresh) {
              const resolved = await resolvePermissions({
                ...fresh,
                permissions: fresh.permissions ?? user.permissions,
                ticketWhatsappIds: fresh.ticketWhatsappIds ?? user.ticketWhatsappIds,
              });
              setUser(resolved);
              writeCachedUser(resolved);
            }
          }
        }
        if (data.action === 'permissions-updated' && Number(data.userId) === Number(user.id)) {
          const fresh = await getCurrentUserInfo();
          if (fresh) {
            // Força re-fetch de /users/:id/permissions para pegar ticketWhatsappIds atualizado.
            // Limpamos permissions para que resolvePermissions não faça early-return.
            const resolved = await resolvePermissions({
              ...fresh,
              permissions: undefined,
              ticketWhatsappIds: undefined,
            });
            setUser(resolved);
            writeCachedUser(resolved);
          }
        }
      };

      io.on(eventName, handleUserEvent);

      return () => {
        io.off(eventName, handleUserEvent);
        // io.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [user]);

  /**
   * Fluxo pós-autenticação: usado por login e signup.
   * Recebe { token, refreshToken } e faz redirect para o app.
   */
  const handleAuthWithTokens = async (authData) => {
    if (splashTimeoutRef.current) {
      clearTimeout(splashTimeoutRef.current);
      splashTimeoutRef.current = null;
    }
    setLoading(true);

    const { token, refreshToken } = authData || {};
    if (!token) {
      setLoading(false);
      throw new Error('Resposta de autenticação inválida');
    }

    writeTokens({ token, refreshToken });

    let user = null;
    try {
      const { data: meData } = await api.get('/auth/me');
      user = await resolvePermissions(meData);
    } catch (_) {
      // /auth/me falhou; redirect e deixa o init carregar
    }

    const company = user?.company;
    if (company && has(company, 'companieSettings') && isArray(company.companieSettings?.[0])) {
      const setting = company.companieSettings[0].find((s) => s.key === 'campaignsEnabled');
      if (setting && setting.value === 'true') {
        localStorage.setItem('cshow', null);
      }
      const signSetting = company.companieSettings[0].find((s) => s.key === 'sendSignMessage');
      if (signSetting && signSetting.value === 'enabled') {
        localStorage.setItem('sendSignMessage', signSetting.value === 'enable');
      }
    }
    if (user?.profileImage) {
      localStorage.setItem('profileImage', user.profileImage);
    }

    if (user) setUser(user);
    setIsAuth(true);
    setLoginOrigin(localStorage.getItem('loginOrigin') || 'default');

    // Bloqueio e aviso de vencimento são responsabilidade do PlanPermissionsContext,
    // que lê user.trial e user.company.status vindos do /auth/me.
    // Aqui apenas redirecionamos para o app — o context decide se exibe bloqueio.
    window.location.href = '/home';
  };

  /**
   * Etapa 1 do login. Com 2FA ativo o backend não emite token: devolve
   * `{ twoFactorRequired, challengeToken }`, e quem chama precisa levar o
   * usuário para a etapa 2. Por isso retorna o resultado em vez de sempre
   * redirecionar.
   *
   * @returns {{ twoFactorRequired?: boolean, twoFactorSetupRequired?: boolean,
   *             challengeToken?: string, failed?: boolean }}
   */
  const handleLogin = async (userData) => {
    try {
      const { data } = await api.post('/auth/login', userData);

      if (data?.twoFactorRequired) {
        setLoading(false);
        return { twoFactorRequired: true, challengeToken: data.challengeToken };
      }

      // A política da empresa exige 2FA e este usuário ainda não configurou:
      // o mesmo desafio abre a tela de configuração bloqueante.
      if (data?.twoFactorSetupRequired) {
        setLoading(false);
        return { twoFactorSetupRequired: true, challengeToken: data.challengeToken };
      }

      await handleAuthWithTokens(data);
      return {};
    } catch (err) {
      toastError(err);
      setLoading(false);
      return { failed: true };
    }
  };

  /**
   * Etapa 2 do login. O `challengeToken` vive só no state de quem chama —
   * nunca no localStorage ao lado dos tokens de sessão.
   *
   * Erros sobem para a tela decidir: ela mostra a mensagem no próprio campo do
   * código, em vez de um toast que tira o foco de quem está digitando.
   */
  const handleVerifyTwoFactor = async ({ challengeToken, code }) => {
    setLoading(true);
    try {
      const data = await verifyTwoFactorChallenge({ challengeToken, code });
      await handleAuthWithTokens(data);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleLogout = () => {
    const origin = localStorage.getItem('loginOrigin') || 'default';

    const doRedirect = () => {
      setIsAuth(false);
      setUser({});
      clearTokens();
      localStorage.removeItem('cshow');
      localStorage.removeItem('loginOrigin');
      clearCachedUser();
      clearPlatformProvidersCache();
      setLoginOrigin('default');
      if (origin === 'mobile') {
        history.push('/mobile-login');
      } else {
        history.push('/login');
      }
    };

    // Captura o token antes de limpar — openApi não passa pelo interceptor de refresh,
    // então um 401 (token expirado) é simplesmente ignorado e o logout local prossegue
    const token = readToken();
    if (token) {
      openApi
        .delete('/auth/logout', { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
    // Limpeza de push em segundo plano (não bloqueia)
    unsubscribeAndUnregister().catch(() => {});
    // Logout e redirect logo em seguida; usuário não fica esperando API/push
    doRedirect();
  };

  const handleSetLoginOrigin = (origin = 'default') => {
    localStorage.setItem('loginOrigin', origin);
    setLoginOrigin(origin);
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch (_) {
      return null;
    }
  };

  const refreshUser = async () => {
    const data = await getCurrentUserInfo();
    if (data) {
      const resolved = await resolvePermissions(data);
      setUser(resolved);
      writeCachedUser(resolved);
      return resolved;
    }
    return data;
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleVerifyTwoFactor,
    handleAuthWithTokens,
    handleLogout,
    handleSetLoginOrigin,
    isMobileSession: loginOrigin === 'mobile',
    getCurrentUserInfo,
    refreshUser,
    socket,
  };
};

export default useAuth;
